import React, { useState, useEffect } from 'react';

const sections = [
  { id: 'cgu', title: 'Conditions Générales d\'Utilisation' },
  { id: 'champ-application', title: '1. Champ d\'application' },
  { id: 'produits-services', title: '2. Produits et Services' },
  { id: 'donnees', title: '3. Utilisation et protection des données' },
  { id: 'evaluation-employeurs', title: '4. Évaluation des employeurs et informations salariales' },
  { id: 'frais-freemium', title: '5. Frais et modèle freemium' },
  { id: 'obligations-utilisateur', title: '6. Obligations de l\'Utilisateur' },
  { id: 'propriete-intellectuelle', title: '7. Propriété Intellectuelle' },
  { id: 'exclusion-garantie', title: '8. Exclusion de Garantie' },
  { id: 'responsabilite', title: '9. Responsabilité' },
  { id: 'duree-resiliation', title: '10. Durée du contrat et Résiliation' },
  { id: 'modification-services', title: '11. Modification de l\'offre de services' },
  { id: 'modification-conditions', title: '12. Modification des présentes conditions' },
  { id: 'droit-applicable', title: '13. Droit applicable et Juridiction' },
  { id: 'app-mobile', title: '14. Conditions particulières pour l\'application mobile' },
  { id: 'signalement', title: '15. Procédures de signalement de contenus illicites' },
];

const GeneralTerms: React.FC = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="px-4 py-10 sm:py-12 max-w-4xl mx-auto">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
        Conditions Générales d'Utilisation
      </h1>
      <p className="text-gray-600 mt-2">
        Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
      </p>

      <nav
        aria-label="Table des matières"
        className="mt-6 rounded-lg border border-gray-200 bg-white p-4"
      >
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Sommaire</h2>
        <ol className="list-inside space-y-1 text-sm">
          {sections.map((s) => (
            <li key={s.id}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(s.id);
                }}
                className="text-indigo-600 hover:text-indigo-800 text-left cursor-pointer bg-transparent border-none p-0 underline"
              >
                {s.title}
              </button>
            </li>
          ))}
        </ol>
      </nav>

      <div className="mt-8 space-y-12 text-gray-800 leading-relaxed">
        <section id="cgu" className="scroll-mt-24">
          <h2 className="text-xl font-bold mb-4 text-gray-900">CONDITIONS GÉNÉRALES</h2>
        </section>

        <section id="champ-application" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">1. Champ d'application</h3>
          <div className="space-y-4">
            <p>
              CVMatchSwiss (ci-après « nous ») propose différentes solutions en matière de 
              recherche d'emploi et de mise en relation candidat-emploi. Les présentes Conditions 
              Générales d'Utilisation (ci-après « CGU ») s'appliquent à l'ensemble des produits et 
              services que nous offrons aux personnes à la recherche d'un emploi (ci-après les « 
              Utilisateurs » ou « candidats »).
            </p>
            <p>
              En utilisant nos services, vous acceptez ces conditions d'utilisation.
            </p>
            <p>
              Ces CGU s'appliquent quel que soit le moyen par lequel l'Utilisateur accède aux 
              services proposés, que ce soit via nos sites Internet ou via notre application mobile (ci-après les « Plateformes »), ou par tout autre canal. Les présentes conditions ne 
              s'appliquent pas aux employeurs, recruteurs ou autres clients professionnels éventuels 
              ; des conditions générales distinctes seront applicables à ces derniers le cas échéant, 
              uniquement les candidats sont visés par ce document.
            </p>
            <p>
              <strong>Note :</strong> Certains produits ou services spécifiques de la plateforme pourraient faire 
              l'objet de conditions d'utilisation additionnelles. Si c'est le cas, l'Utilisateur en sera 
              informé au moment d'utiliser ces produits/services particuliers.
            </p>
          </div>
        </section>

        <section id="produits-services" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">2. Produits et Services</h3>
          
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold mb-2">2.1 Création d'un compte</h4>
              <div className="space-y-3">
                <p>
                  Chaque Utilisateur a la possibilité de créer un compte personnel gratuit afin de profiter 
                  pleinement de nos services. Pour créer un compte, l'Utilisateur doit fournir au 
                  minimum une adresse e-mail valide ainsi qu'un mot de passe (ou utiliser un mode 
                  d'inscription tiers autorisé, par exemple via Google ou Apple le cas échéant).
                </p>
                <p>
                  Lors de l'inscription, l'Utilisateur devra accepter les présentes conditions d'utilisation 
                  ainsi que notre politique de confidentialité. En cliquant sur le bouton de création de 
                  compte (par exemple « S'inscrire » ou « Continuer »), l'Utilisateur confirme qu'il 
                  accepte les présentes conditions et envoie sa demande de création de compte. Nous 
                  examinerons la demande et créerons le compte. Un e-mail de confirmation pourra être 
                  envoyé à l'Utilisateur (y compris un lien de vérification d'adresse e-mail). Une fois le 
                  compte créé et l'adresse e-mail vérifiée, une relation contractuelle est établie entre 
                  l'Utilisateur et nous au titre des présentes CGU.
                </p>
                <p>
                  Une fois son compte créé, l'Utilisateur a la possibilité de compléter son profil avec des 
                  informations supplémentaires (par ex. coordonnées, parcours, compétences, CV et 
                  autres documents) et d'utiliser l'ensemble des produits et services réservés aux 
                  utilisateurs enregistrés.
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">2.2 Services proposés</h4>
              <div className="space-y-3">
                <p>
                  Notre plateforme aide les candidats à postuler mieux et plus vite grâce à l'intelligence 
                  artificielle. Les services principaux proposés aux candidats comprennent notamment :
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Analyse intelligente CV - Offre</strong> – L'Utilisateur peut importer son CV et coller une offre 
                    d'emploi pour obtenir une analyse automatisée de compatibilité. L'IA est entraînée sur 
                    plus de 1 500 CV suisses ayant abouti à des embauches, calcule un score de 
                    compatibilité et identifie les écarts clés (compétences, expérience, formation, langues).
                  </li>
                  <li>
                    <strong>Recommandations personnalisées</strong> – Sur la base de l'analyse, nous proposons des 
                    améliorations concrètes et actionnables pour optimiser le profil du candidat et 
                    améliorer ses chances de succès.
                  </li>
                  <li>
                    <strong>Génération de lettres de motivation</strong> – En un clic, l'Utilisateur peut générer une lettre de 
                    motivation personnalisée pour l'offre analysée, avec possibilité d'ajuster le ton et la 
                    longueur selon ses préférences.
                  </li>
                  <li>
                    <strong>Export et partage</strong> – L'Utilisateur peut exporter ses analyses et lettres de motivation au 
                    format PDF et les partager selon ses besoins.
                  </li>
                  <li>
                    <strong>Historique des analyses</strong> – Nous conservons un historique des analyses effectuées par 
                    l'Utilisateur pour lui permettre de suivre ses candidatures et l'évolution de son profil au 
                    fil du temps.
                  </li>
                  <li>
                    <strong>Contrôle des données</strong> – L'Utilisateur dispose d'un contrôle total sur ses données et peut 
                    supprimer son CV et tous les résultats d'analyses à tout moment depuis son compte.
                  </li>
                </ul>
                <p>
                  Nous nous réservons le droit d'ajouter de nouveaux services ou d'améliorer les services 
                  existants à tout moment, ou au contraire de modifier ou interrompre certains services. 
                  L'Utilisateur ne peut pas exiger le maintien d'un service ou d'une fonctionnalité 
                  particulière si nous décidons de le modifier ou supprimer. En cas de modification 
                  significative ou de suppression d'un service, si l'Utilisateur n'est pas d'accord, il peut 
                  résilier son compte et cesser d'utiliser la plateforme conformément à l'article 10 des 
                  présentes CGU.
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">2.3 Utilisation des services sans compte</h4>
              <div className="space-y-3">
                <p>
                  Certains services sont accessibles sans qu'il soit nécessaire de créer un compte. En 
                  particulier, tout visiteur non enregistré peut accéder aux fonctionnalités de base et 
                  effectuer des analyses ponctuelles. Il peut également, dans certains cas, utiliser 
                  certaines fonctionnalités sans compte en fournissant manuellement les informations 
                  requises.
                </p>
                <p>
                  En revanche, l'accès aux services avancés (tels que l'historique des analyses, la 
                  sauvegarde des profils candidats, ou l'accès à des analyses supplémentaires au-delà du 
                  quota gratuit) nécessite la création préalable d'un compte. De même, certains services 
                  spécifiques pourraient nécessiter l'acceptation de conditions additionnelles, ce dont 
                  l'Utilisateur sera informé s'il souhaite y souscrire.
                </p>
                <p>
                  Nous nous réservons le droit de modifier, d'étendre ou d'interrompre à tout moment les 
                  services accessibles sans compte. Si l'Utilisateur n'accepte pas une modification de ces 
                  services libres d'accès, il lui suffit de cesser d'utiliser le site et les services concernés.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="donnees" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">3. Utilisation et protection des données</h3>
          <div className="space-y-4">
            <p>
              Nous accordons une grande importance à la protection des données personnelles de 
              nos Utilisateurs. Nous traitons les données personnelles des candidats avec la plus 
              grande diligence et conformément aux lois et réglementations applicables en matière 
              de protection des données. Le traitement des données des résidents suisses est 
              prioritairement régi par la Loi fédérale sur la protection des données (LPD), tandis que 
              celui des résidents de l'Espace économique européen est régi par le Règlement général 
              sur la protection des données (RGPD).
            </p>
            <p>
              Les données personnelles collectées sont utilisées exclusivement pour fournir les 
              services demandés par l'Utilisateur et assurer le bon fonctionnement de la plateforme 
              (par exemple, utiliser les informations du profil pour analyser la compatibilité avec les 
              offres d'emploi ou améliorer les algorithmes d'IA). Pour plus de détails sur la gestion 
              des données personnelles (données collectées, finalités, droits des personnes, durées de 
              conservation, etc.), l'Utilisateur est invité à consulter la Politique de Confidentialité 
              qui fait partie intégrante des présentes conditions.
            </p>
            <p>
              En utilisant la plateforme, l'Utilisateur consent en outre à ce que nous utilisions, de 
              manière anonyme ou agrégée, les données qu'il fournit, à des fins d'analyse statistique, 
              d'amélioration du service ou de développement de nouvelles fonctionnalités. 
              L'Utilisateur nous accorde ainsi le droit d'exploiter sans limitation les données le 
              concernant une fois celles-ci anonymisées, c'est-à-dire ne permettant plus de 
              l'identifier personnellement. Nous pourrons par exemple utiliser ces données 
              anonymisées pour observer des tendances du marché de l'emploi, publier des rapports 
              ou entraîner nos algorithmes d'analyse de CV, tout en veillant à ce qu'aucune donnée 
              personnelle identifiable ne soit divulguée.
            </p>
            <p>
              Pour toute question relative à la protection des données, ou pour exercer ses droits 
              d'accès, de rectification, d'opposition, de suppression, etc., l'Utilisateur peut nous 
              contacter selon les modalités précisées dans la Politique de Confidentialité.
            </p>
          </div>
        </section>

        <section id="evaluation-employeurs" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">4. Évaluation des employeurs et informations salariales</h3>
          <div className="space-y-4">
            <p>
              Nous pouvons offrir à nos Utilisateurs la possibilité de partager du contenu lié à leur 
              expérience candidat, par exemple en évaluant des employeurs (entreprises) ou en 
              fournissant des informations salariales de façon anonyme. Si de telles fonctionnalités 
              existent sur la plateforme (par exemple une section d'avis sur les entreprises, ou de 
              comparaison de salaires), l'Utilisateur s'engage à respecter strictement les règles 
              suivantes lors de la soumission de contenu :
            </p>
            <ul className="list-disc pl-6 space-y-3">
              <li>
                <strong>Authenticité de l'expérience :</strong> L'Utilisateur ne peut évaluer que des employeurs auprès 
                desquels il a réellement travaillé, travaille encore ou a postulé (et a éventuellement été 
                convié à un entretien). Il est interdit de publier des évaluations sur des entreprises avec 
                lesquelles l'Utilisateur n'a eu aucun contact professionnel direct.
              </li>
              <li>
                <strong>Exactitude des informations :</strong> Toutes les informations fournies dans l'évaluation ou le 
                commentaire doivent être complètes, à jour et conformes à la réalité. Aucune fausse 
                déclaration ni information trompeuse concernant l'employeur, ses employés, le poste 
                occupé ou tout autre fait relatif à l'expérience de travail ne doit être faite. De même, 
                toute donnée salariale communiquée (par exemple un niveau de rémunération pour un 
                poste donné) doit être exacte et véridique.
              </li>
              <li>
                <strong>Respect de la vie privée :</strong> Aucune évaluation ne doit contenir le nom de personnes 
                physiques ni d'informations permettant d'identifier une personne précise (autre que 
                l'auteur lui-même). Il est interdit de mentionner explicitement des collègues, 
                supérieurs hiérarchiques ou recruteurs par leur nom, ou de divulguer des données 
                personnelles les concernant.
              </li>
              <li>
                <strong>Contenu approprié :</strong> L'Utilisateur ne doit pas inclure de contenu injurieux, obscène, 
                offensant, diffamatoire ou illicite dans ses évaluations. Sont prohibés notamment les 
                propos à caractère haineux, raciste, sexiste, discriminatoire ou pornographique. De 
                plus, il n'est pas permis de faire la publicité d'autres produits ou services externes dans 
                le cadre d'une évaluation (par ex. inclure des liens ou références promotionnelles vers 
                un site tiers).
              </li>
              <li>
                <strong>Utilisation des données :</strong> En soumettant une évaluation d'employeur ou une 
                information salariale, l'Utilisateur accepte que nous puissions modérer, éditer ou 
                raccourcir son contenu avant publication, notamment pour respecter les présentes 
                règles ou des contraintes de présentation. Nous nous réservons le droit de refuser la 
                publication d'une évaluation ou de la supprimer à tout moment, sans avoir à nous 
                justifier. L'Utilisateur consent également à ce que nous puissions republier ou diffuser 
                son évaluation (par exemple, sur d'autres sites ou supports partenaires) et la 
                communiquer à des tiers, sous réserve que cela se fasse de manière anonyme (aucune 
                donnée personnelle de l'auteur ne sera dévoilée sans consentement explicite).
              </li>
            </ul>
            <p>
              En cas de manquement à ces règles, nous pourrons retirer le contenu incriminé et nous 
              réservons la possibilité de suspendre ou supprimer le compte de l'Utilisateur concerné.
            </p>
          </div>
        </section>

        <section id="frais-freemium" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">5. Frais et modèle freemium</h3>
          <div className="space-y-4">
            <p>Notre service fonctionne selon un modèle freemium adapté aux besoins des candidats :</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Services gratuits :</strong> Chaque Utilisateur bénéficie de 5 analyses CV - Offre gratuites par mois 
                calendaire. Ces analyses incluent le score de compatibilité, les recommandations 
                d'amélioration et la génération de lettres de motivation.
              </li>
              <li>
                <strong>Services payants :</strong> Si l'Utilisateur souhaite effectuer plus de 5 analyses par mois, des packs 
                d'analyses supplémentaires sont disponibles à l'achat via l'application. Les prix et conditions 
                de ces packs sont clairement affichés dans l'interface utilisateur avant tout achat.
              </li>
              <li>
                <strong>Facturation :</strong> Les achats de packs d'analyses sont facturés selon les conditions de l'App Store 
                (iOS) ou du Google Play Store (Android) selon la plateforme utilisée. Aucun abonnement 
                récurrent n'est activé sans consentement explicite de l'Utilisateur.
              </li>
              <li>
                <strong>Remboursements :</strong> Les demandes de remboursement sont traitées selon les politiques des 
                plateformes de distribution (Apple App Store, Google Play Store). Nous n'offrons pas de 
                remboursements directs mais pouvons examiner les demandes au cas par cas en cas de 
                dysfonctionnement technique avéré.
              </li>
            </ul>
          </div>
        </section>

        <section id="obligations-utilisateur" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">6. Obligations de l'Utilisateur</h3>
          <div className="space-y-4">
            <p>
              En utilisant la plateforme et ses services, l'Utilisateur s'engage à respecter les obligations 
              suivantes afin de garantir le bon fonctionnement du service et une expérience positive pour 
              tous :
            </p>
            
            <div>
              <h4 className="font-semibold mb-2">Utilisation autorisée</h4>
              <ul className="list-disc pl-6 space-y-1">
                <li>Fournir des informations exactes, complètes et à jour lors de la création du compte et dans son profil (données personnelles, expérience, qualifications, etc.).</li>
                <li>Utiliser son compte à des fins personnelles uniquement, c'est-à-dire pour sa propre recherche d'emploi et gestion de carrière.</li>
                <li>Respecter les autres utilisateurs et interlocuteurs en conservant une attitude professionnelle et courtoise dans les communications.</li>
                <li>Protéger la confidentialité de ses identifiants : conserver son mot de passe en lieu sûr et immédiatement nous informer en cas d'accès non autorisé à son compte ou de suspicion de piratage.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Utilisation interdite</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Fournir des informations fausses, incomplètes ou trompeuses dans son profil ou lors de l'utilisation des services (sous peine de résiliation du compte). Par exemple, l'Utilisateur ne doit pas mentir sur ses qualifications, expériences ou identité.</li>
                <li>Partager ses accès ou laisser un tiers utiliser son compte. Le compte est strictement personnel ; il est interdit de prêter, louer ou transmettre ses identifiants à quiconque. L'Utilisateur est responsable de toutes les actions effectuées via son compte.</li>
                <li>Publier ou transmettre des contenus illicites ou inappropriés via notre plateforme. Sont notamment prohibés les contenus ou fichiers à caractère diffamatoire, obscène, pédopornographique, sexuellement explicite, haineux, discriminatoire, violent ou incitant à des activités illégales.</li>
                <li>Détourner la plateforme de son objet. L'Utilisateur ne doit pas utiliser nos services à des fins autres que la recherche d'emploi et l'amélioration de ses candidatures.</li>
                <li>Perturber le fonctionnement du site ou de l'application. Il est interdit d'entreprendre toute action nuisant à la sécurité ou à la performance de notre infrastructure.</li>
              </ul>
            </div>

            <p>
              En cas de violation de ces obligations par un Utilisateur, nous nous réservons le droit de 
              suspendre temporairement ou définitivement le compte de l'Utilisateur fautif, et/ou de résilier 
              le contrat d'utilisation conformément à l'article 10 ci-dessous. Des poursuites judiciaires 
              pourraient également être engagées en cas d'infraction grave (par ex. acte de piratage 
              informatique, diffusion de contenu illégal, etc.).
            </p>
          </div>
        </section>

        <section id="propriete-intellectuelle" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">7. Propriété Intellectuelle</h3>
          <div className="space-y-4">
            <p>
              Nous conservons (ou nos partenaires et fournisseurs le cas échéant) l'intégralité des droits de 
              propriété intellectuelle et des droits de propriété sur la plateforme, les sites Internet et 
              applications mobiles, y compris sur tous les contenus et éléments qui les composent. Cela 
              comprend notamment les droits d'auteur sur les textes, images, logos, graphismes, design, 
              mises en page, logiciels, bases de données, ainsi que les droits sur les marques, noms 
              commerciaux, noms de domaine et autres signes distinctifs appartenant à notre entreprise.
            </p>
            <p>
              Nous accordons aux Utilisateurs une licence d'utilisation limitée, non exclusive et non 
              transférable de la plateforme et de ses services, strictement pour un usage conforme aux 
              présentes CGU (c'est-à-dire pour la recherche d'emploi et les activités associées, dans le cadre 
              personnel du candidat). Toute utilisation en dehors de ce cadre est interdite sans l'autorisation 
              écrite préalable. En particulier, il est interdit de reproduire, copier, modifier, diffuser, 
              transmettre, afficher, publier, décompiler ou réutiliser tout ou partie du site/app ou de son 
              contenu à des fins commerciales ou collectives sans accord exprès. De même, le web scraping 
              ou l'extraction automatisée de données sans permission est prohibé.
            </p>
            <p>
              Toute violation de ces droits de propriété intellectuelle pourra entraîner des poursuites. 
              L'Utilisateur s'engage à respecter ces droits et à nous notifier s'il constate une atteinte aux 
              droits de propriété intellectuelle sur la plateforme.
            </p>
          </div>
        </section>

        <section id="exclusion-garantie" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">8. Exclusion de Garantie</h3>
          <div className="space-y-4">
            <p>
              Dans le cadre de l'utilisation de ses services, nous nous efforçons de fournir une expérience de 
              qualité, mais ne donnons aucune garantie expresse ou implicite quant à la plateforme, qui est 
              fournie « en l'état ». En particulier, nous ne garantissons pas l'exactitude, l'exhaustivité ni 
              l'actualité des contenus consultables via la plateforme (analyses d'IA, recommandations, 
              informations sur les salaires ou les entreprises, etc.), ni leur adéquation aux besoins 
              spécifiques de l'Utilisateur. Les informations mises à disposition (par nos services ou par des 
              algorithmes d'IA) sont données à titre indicatif et peuvent comporter des erreurs ou omissions, 
              malgré tous nos efforts de vérification.
            </p>
            <p>
              Nous ne garantissons pas non plus que les analyses effectuées via la plateforme aboutiront à 
              une amélioration des candidatures ou à une embauche. La responsabilité du succès des 
              candidatures revient entièrement à l'Utilisateur et nous n'intervenons pas dans le processus de 
              recrutement lui-même. L'Utilisateur comprend que notre service est un outil d'aide à la 
              candidature utilisant l'intelligence artificielle et ne peut s'engager sur aucun résultat en termes 
              d'embauche.
            </p>
            <p>
              En outre, nous ne garantissons pas que les sites et applications fonctionneront de manière 
              ininterrompue et sans erreurs ou pannes. Nous mettons en place des sauvegardes régulières de 
              nos systèmes, mais une perte de données ou une interruption de service imprévue ne peut être 
              totalement exclue. Nous ne pouvons pas être tenus responsables des conséquences 
              d'éventuelles indisponibilités temporaires du service, de bugs, d'attaques externes, ou de toute 
              défaillance technique indépendante de notre volonté.
            </p>
            <p>
              Dans toute la mesure permise par la loi, nous excluons ainsi toute garantie implicite relative à 
              la qualité marchande, l'adéquation à un usage particulier, la disponibilité ou la non-violation. 
              L'Utilisateur utilise la plateforme à ses propres risques.
            </p>
          </div>
        </section>

        <section id="responsabilite" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">9. Responsabilité</h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold mb-2">Limitation de responsabilité</h4>
              <p>
                <strong>IMPORTANT :</strong> Notre service est exploité en tant qu'entreprise individuelle. En vertu du droit 
                suisse, l'exploitant assume une responsabilité personnelle et illimitée pour les activités de 
                l'entreprise.
              </p>
              <p>
                Nous nous efforçons de fournir un service de qualité mais ne pouvons garantir des résultats 
                d'embauche. Notre responsabilité est limitée aux dommages directs causés intentionnellement 
                ou par faute grave, dans la mesure permise par la loi suisse applicable aux entreprises 
                individuelles.
              </p>
              <p>
                Nous ne serons en aucun cas responsables des dommages indirects ou imprévisibles subis par 
                l'Utilisateur dans le cadre de l'utilisation de la plateforme (tels que perte d'opportunité, perte 
                de revenu, préjudice d'image, ou dommage résultant de la perte de données), sauf disposition 
                légale impérative contraire.
              </p>
              <p>
                L'Utilisateur reconnaît que notre service est un outil d'aide à la candidature et que le succès 
                d'une candidature dépend de nombreux facteurs indépendants de la plateforme. Nous ne 
                garantissons aucun résultat spécifique en termes d'embauche ou de réponses positives aux 
                candidatures.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Contenus tiers et liens externes</h4>
              <p>
                La plateforme peut contenir des liens hypertextes renvoyant vers des sites web de tiers. Nous 
                n'exerçons aucun contrôle sur le contenu de ces sites externes et ne saurions en garantir 
                l'exactitude, l'exhaustivité, la légalité ou la fiabilité. En conséquence, nous déclinons toute 
                responsabilité quant aux contenus de tiers. L'Utilisateur suit ces liens et utilise les services de 
                tiers à ses propres risques.
              </p>
              <p>
                Toutefois, nous pouvons retirer sans préavis tout contenu ou lien externe qui nous serait 
                signalé comme illicite ou inapproprié. Si un Utilisateur découvre un contenu illégal ou portant 
                atteinte à des droits sur la plateforme, il est invité à nous en informer immédiatement. Dès 
                notification par un Utilisateur de l'existence d'un contenu manifestement illicite, nous agirons 
                promptement pour le supprimer ou en bloquer l'accès.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Force majeure</h4>
              <p>
                Nous ne pourrons être tenus responsables de l'inexécution ou de la mauvaise exécution de nos 
                obligations résultant d'un cas de force majeure ou d'un événement échappant à notre contrôle 
                raisonnable (tels que catastrophes naturelles, incidents techniques majeurs, pannes 
                d'électricité, cyberattaques de grande ampleur, conflits sociaux, interventions 
                gouvernementales, etc.).
              </p>
            </div>
          </div>
        </section>

        <section id="duree-resiliation" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">10. Durée du contrat et Résiliation</h3>
          <div className="space-y-6">
            <p>
              Les présentes conditions d'utilisation entrent en vigueur dès la création du compte par 
              l'Utilisateur (ou dès sa première utilisation des services, s'il n'a pas de compte) et restent 
              valables pour une durée indéterminée tant que l'Utilisateur utilise la plateforme. Le contrat 
              d'utilisation demeure effectif jusqu'à sa résiliation par l'une des parties selon les modalités ci-dessous.
            </p>

            <div>
              <h4 className="font-semibold mb-2">Résiliation par l'Utilisateur</h4>
              <p>
                L'Utilisateur peut décider à tout moment de mettre fin à l'utilisation du service et de résilier 
                son compte. La résiliation peut se faire soit en supprimant son compte via l'interface prévue 
                dans les paramètres du profil, soit en en faisant la demande écrite au support (par e-mail). En 
                cas de résiliation, nous procéderons à la désactivation du compte et à la suppression définitive 
                des données personnelles associées, dans les meilleurs délais.
              </p>
              <p>
                Néanmoins, certaines données pourront être conservées temporairement si elles sont 
                nécessaires pour exécuter des obligations contractuelles en cours ou pour satisfaire à des 
                obligations légales de conservation (par ex. sauvegarde légale, obligations fiscales ou de 
                sécurité). En dehors de ces cas, l'ensemble des données du profil de l'Utilisateur sera effacé de 
                manière irréversible.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Résiliation par nous</h4>
              <p>
                Nous nous réservons le droit de suspendre ou clôturer le compte d'un Utilisateur en cas de 
                manquement grave ou répété aux présentes CGU (voir article 6), ou si l'Utilisateur adopte un 
                comportement nuisible envers la plateforme ou d'autres utilisateurs. En règle générale, nous 
                adresserons un avertissement préalable à l'Utilisateur et pourrons, si le problème n'est pas 
                résolu, résilier le contrat et supprimer le compte fautif. En outre, nous pouvons décider de 
                résilier un compte inactif depuis une longue durée (par exemple plus de 24 mois) après en 
                avoir informé l'Utilisateur, ou de résilier l'accès de tous les Utilisateurs en cas d'arrêt définitif 
                du service.
              </p>
              <p>
                En cas de suppression d'un compte (par l'Utilisateur ou par nous), les données personnelles 
                seront traitées comme indiqué ci-dessus. Nous pourrons néanmoins, au lieu de la suppression, 
                anonymiser certaines données issues du compte, afin de les conserver à des fins statistiques ou 
                d'amélioration du service. Conformément aux articles 3 et 5 ci-dessus, l'Utilisateur a accordé 
                le droit d'utiliser ces données anonymisées de manière illimitée. Aucune donnée nominative 
                identifiable ne sera toutefois conservée sans le consentement de l'Utilisateur, hormis pour les 
                stricts besoins légaux ou contractuels.
              </p>
            </div>
          </div>
        </section>

        <section id="modification-services" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">11. Modification de l'offre de services</h3>
          <div className="space-y-4">
            <p>
              Notre service évolue en permanence pour s'adapter aux besoins du marché et aux avancées 
              technologiques. Par conséquent, nous nous réservons le droit de modifier, suspendre ou 
              arrêter à tout moment tout ou partie des produits et services proposés sur la plateforme. Par 
              exemple, nous pouvons ajouter de nouvelles fonctionnalités, en retirer certaines, changer les 
              caractéristiques d'un service ou arrêter un service obsolète.
            </p>
            <p>
              Dans la mesure du possible, nous informerons les Utilisateurs des modifications ou de l'arrêt 
              d'un service qui les concernent (par exemple via le site ou par e-mail). Toutefois, nous 
              n'offrons aucune garantie de maintien d'une fonctionnalité spécifique et ne pourrons être tenus 
              pour responsables de la suppression ou modification d'un service. L'Utilisateur ne pourra 
              prétendre à aucune indemnisation ou compensation du fait de la modification ou de l'arrêt de 
              tout ou partie des services. Si l'Utilisateur n'est pas en accord avec une modification de 
              service, il lui appartient de cesser d'utiliser le service concerné ou de résilier son compte si 
              nécessaire.
            </p>
          </div>
        </section>

        <section id="modification-conditions" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">12. Modification des présentes conditions</h3>
          <div className="space-y-6">
            <p>
              Nous pouvons être amenés à modifier les présentes Conditions Générales d'Utilisation, par 
              exemple pour les adapter à l'évolution de la législation ou des services proposés. Toute 
              nouvelle version des CGU sera communiquée aux Utilisateurs selon les modalités ci-dessous :
            </p>

            <div>
              <h4 className="font-semibold mb-2">Utilisateurs ayant un compte</h4>
              <p>
                En cas de modification des CGU, nous informerons l'Utilisateur titulaire d'un compte par un 
                avis écrit (par exemple par e-mail, ou via une notification sur la plateforme) au moins deux 
                (2) semaines avant la date d'entrée en vigueur des nouvelles conditions. L'avis contiendra soit 
                le nouveau texte complet des CGU, soit un lien vers celui-ci, ainsi qu'un résumé des 
                changements importants.
              </p>
              <p>
                L'Utilisateur a le droit de s'opposer aux modifications proposées avant la date d'entrée en 
                vigueur indiquée, en notifiant son refus par écrit. Si l'Utilisateur ne manifeste pas son 
                désaccord dans le délai imparti (absence de réponse ou absence d'opposition explicite), il sera 
                considéré comme ayant accepté les nouvelles conditions, qui deviendront alors applicables à 
                son égard à la date d'effet prévue. En cas de refus de l'Utilisateur, nous nous réservons le 
                choix soit de maintenir l'ancienne version des conditions pour cet Utilisateur (dans la limite 
                du raisonnable), soit de résilier le contrat qui le lie à l'Utilisateur à la date d'entrée en vigueur 
                des modifications (ce qui entraînera la fermeture du compte utilisateur). Nous préciserons 
                dans la notification la procédure à suivre pour accepter ou refuser ces nouvelles conditions.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Utilisateurs sans compte</h4>
              <p>
                Pour les Utilisateurs qui utilisent le service sans compte (simple visiteur), les CGU 
                applicables sont toujours celles en vigueur au moment de l'accès aux services. La version 
                actuelle des conditions d'utilisation sera affichée sur le site et l'application, consultable à tout 
                moment par le public. Nous pouvons modifier ces conditions à tout moment. Il appartient 
                donc à l'Utilisateur sans compte de vérifier les CGU à jour lors de chaque utilisation. Le fait 
                de continuer à utiliser le site sans être inscrit vaut acceptation des conditions en vigueur. S'il 
                n'accepte pas les changements, l'Utilisateur doit s'abstenir d'utiliser la plateforme.
              </p>
            </div>
          </div>
        </section>

        <section id="droit-applicable" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">13. Droit applicable et Juridiction</h3>
          <div className="space-y-4">
            <p>
              Les présentes Conditions Générales d'Utilisation sont soumises exclusivement au droit suisse. 
              Cela signifie que l'interprétation et l'exécution des présentes CGU, ainsi que tous les litiges en 
              découlant, seront régis par la loi suisse.
            </p>
            <p>
              En cas de différend relatif à l'utilisation de nos services ou aux présentes conditions, et à 
              défaut de résolution amiable, les tribunaux compétents de Zurich (Suisse) seront seuls 
              compétents. Toutefois, nous nous réservons le droit d'intenter une action devant les tribunaux 
              du domicile de l'Utilisateur si celui-ci réside dans un pays différent, notamment pour faire 
              exécuter une obligation ou en cas d'atteinte à nos droits. Cette clause de juridiction est 
              acceptée par l'Utilisateur sous réserve de dispositions impératives éventuelles plus favorables 
              dans son pays de résidence habituelle.
            </p>
          </div>
        </section>

        <section id="app-mobile" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">14. Conditions particulières pour l'application mobile</h3>
          <div className="space-y-6">
            <p>
              Les dispositions suivantes s'appliquent spécifiquement à l'utilisation de l'application mobile, 
              lorsqu'elle est téléchargée via des plateformes tierces de distribution d'applications (ci-après 
              les « Canaux de distribution »), notamment l'Apple App Store pour les appareils iOS et le 
              Google Play Store pour les appareils Android. Lorsque l'Utilisateur obtient l'application par le 
              biais de l'un de ces canaux, il peut être soumis aux conditions d'utilisation propres à ce canal 
              en plus des présentes CGU. Il est entendu que ces CGU constituent un accord exclusivement 
              entre vous (l'Utilisateur) et nous, et non avec le fournisseur du canal de distribution (Apple, 
              Google, ou autre).
            </p>

            <div>
              <h4 className="font-semibold mb-2">Pour les utilisateurs d'iPhone/iPad (Apple App Store)</h4>
              <div className="space-y-3">
                <p>
                  Vous reconnaissez que les présentes conditions d'utilisation sont conclues entre vous et nous 
                  uniquement, Apple n'étant pas partie à cet accord. Nous sommes seuls responsables de 
                  l'application mobile et de son contenu sur iOS, et non Apple. L'utilisation de l'application iOS 
                  doit se faire en conformité avec les Règles d'utilisation de l'App Store d'Apple.
                </p>
                <p>
                  La licence d'utilisation qui vous est accordée pour l'application sur iOS est limitée : elle est 
                  non transférable et autorise l'utilisation de l'application sur tout appareil Apple iOS que vous 
                  possédez ou contrôlez, conformément aux Conditions d'utilisation de l'App Store. Vous 
                  acceptez également de respecter toutes les conditions du contrat utilisateur Apple qui vous est 
                  applicable (par exemple, les conditions générales des services Apple Media).
                </p>
                <p>
                  Apple n'assume aucune obligation de maintenance ni de support technique vis-à-vis de 
                  l'application. En cas de défaillance de l'application à se conformer à une garantie applicable, 
                  l'Utilisateur peut en informer Apple ; le cas échéant (si l'application était payante), Apple 
                  pourra vous rembourser le prix d'achat de l'application, étant entendu qu'à part ce éventuel 
                  remboursement, Apple n'aura aucune autre obligation de garantie ou de responsabilité relative 
                  à l'application. Dans la mesure permise par la loi, Apple décline toute autre garantie explicite 
                  ou implicite concernant l'application.
                </p>
                <p>
                  L'Utilisateur reconnaît que nous sommes responsables du traitement de toutes les 
                  réclamations liées à l'application ou à son utilisation, et non Apple. Ainsi, pour toute plainte 
                  de l'Utilisateur ou d'un tiers relative à l'application (par exemple : responsabilité produit, non-conformité légale, violation de droits de propriété intellectuelle par l'application, ou questions 
                  de protection des consommateurs), nous assumerons l'entière responsabilité du suivi de la 
                  réclamation. Apple ne sera pas responsable du traitement de ces plaintes.
                </p>
                <p>
                  Enfin, l'Utilisateur reconnaît et accepte qu'Apple et ses filiales sont considérés comme des 
                  tiers bénéficiaires des présentes conditions en ce qui concerne l'application iOS, et qu'en 
                  acceptant ces CGU, Apple aura la faculté d'en faire appliquer les termes envers l'Utilisateur en 
                  tant que bénéficiaire tiers. Autrement dit, si l'Utilisateur viole ces conditions et que cela 
                  affecte les intérêts d'Apple, cette dernière dispose du droit d'en exiger le respect.
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Pour les utilisateurs d'Android (Google Play Store)</h4>
              <div className="space-y-3">
                <p>
                  De même, en téléchargeant l'application depuis Google Play, vous acceptez de vous 
                  conformer aux conditions d'utilisation de Google Play en plus des présentes CGU. Google 
                  n'est pas partie à ce contrat utilisateur et nous sommes seuls responsables de l'application et de 
                  son contenu sur la plateforme Android. Google n'assume aucune obligation de support 
                  technique ou de maintenance vis-à-vis de l'application, et ne fournit aucune garantie la 
                  concernant.
                </p>
                <p>
                  En cas de non-conformité de l'application à une obligation légale ou à vos attentes, toute 
                  réclamation devra nous être adressée (et non à Google). Comme pour Apple, vous 
                  reconnaissez que Google LLC et ses affiliés sont susceptibles d'être considérés comme des 
                  tiers bénéficiaires des présentes CGU pour l'application Android, et qu'en acceptant ces 
                  conditions, vous acceptez également que Google ait le droit d'en exiger le respect en tant que 
                  tiers bénéficiaire (dans la mesure des exigences de Google Play).
                </p>
              </div>
            </div>

            <p>
              Ces dispositions spécifiques n'affectent pas les autres clauses des présentes Conditions 
              Générales, qui restent pleinement applicables à l'utilisation de l'application mobile.
            </p>
          </div>
        </section>

        <section id="signalement" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">15. Procédures de signalement de contenus illicites</h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold mb-2">15.1 Signalement de contenus problématiques</h4>
              <p>
                Les contenus illicites ou inappropriés peuvent être signalés à l'adresse e-mail suivante : 
                support@cvmatchswiss.ch. Il convient de fournir des informations aussi précises que possible 
                sur l'identification du contenu problématique et la justification de son caractère illicite ou 
                inapproprié.
              </p>
              <p>
                Nous vérifions si le signalement contient suffisamment d'informations pour pouvoir juger de 
                la nature du contenu signalé. Si le signalement ne contient pas toutes les informations 
                nécessaires à l'examen, nous en informons la personne qui a fait le signalement.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">15.2 Traitement et mesures</h4>
              <div className="space-y-3">
                <p>
                  Après réception d'un signalement, nous vérifions que le contenu signalé est conforme à la 
                  législation en vigueur et aux présentes CGU. Dans la mesure où nous estimons que le contenu 
                  est illicite ou inapproprié, nous pouvons prendre les mesures suivantes :
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Suppression ou modification du contenu concerné</li>
                  <li>Suspension temporaire ou définitive du compte de l'Utilisateur responsable</li>
                  <li>Signalement aux autorités compétentes si nécessaire</li>
                </ul>
                <p>
                  Nous nous engageons à traiter tout signalement dans un délai raisonnable et à informer 
                  l'auteur du signalement des mesures prises, dans le respect de la confidentialité et de la 
                  protection des données.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-indigo-600 hover:bg-indigo-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 ease-in-out hover:scale-110 z-50"
          aria-label="Scroll to top"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default GeneralTerms;
